import { ExportOutlined } from '@ant-design/icons'
import { DeviceStatus } from '@lmc/ts-common'
import {
  Card,
  EmptyState,
  NotStartedIcon,
  PageContentWrapper,
  PageHeader,
  PermissionIcon
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import type { TablePaginationConfig } from 'antd'
import { Switch } from 'antd'
import { useAbilityAccess } from 'components/AccessControl'
import useDevicesAccess from 'components/AccessControl/features/useDevicesAccess'
import { useToggleHomeFeature } from 'components/AccessControl/features/useToggleHomeFeature'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Spinner } from '../../../../components/generic'
import { useDevices } from '../../hooks'
import type { TableParams } from '../../types'
import { tableParamsDefaultState } from '../../utils/devices'
import DeviceSignsPanel from './DeviceSignsPanel'
import DevicesTable from './DevicesTable'
import { HelpTextLink } from './HelpTextLink'
import StatusSwitch, {
  type ActiveTab,
  ALL_DEVICES_TAB,
  SIGNS_TAB
} from './StatusSwitch'

const HELP_DISABLED =
  'https://support.logmycare.co.uk/en/articles/8010663-how-do-i-set-up-device-access-control'

const HELP_ENABLED =
  'https://support.logmycare.co.uk/en/articles/8010670-how-do-i-approve-a-new-device-for-my-care-home'

export const DeviceAccessScreen: FC = () => {
  const navigate = useNavigate()

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const hasDeviceAccessControl = useDevicesAccess()

  const { toggleFeature, isSaving } = useToggleHomeFeature()

  const hasRolePermission = useAbilityAccess([
    { i: 'update', this: 'MobileDevice' }
  ])

  const [status, setStatus] = useState<DeviceStatus | undefined>(
    DeviceStatus.pending
  )

  const [activeTab, setActiveTab] = useState<ActiveTab>(
    hasDeviceAccessControl ? DeviceStatus.pending : ALL_DEVICES_TAB
  )

  const [tableParams, setTableParams] = useState<TableParams>(
    tableParamsDefaultState()
  )

  const { devices: devicesAllStatuses, isLoading: isLoadingAllStatuses } =
    useDevices(undefined, hasRolePermission)

  const hasAnyStatusDevices =
    !isLoadingAllStatuses && !!devicesAllStatuses?.length

  const { devices, isLoading, count, refetch } = useDevices(
    status,
    hasRolePermission && hasDeviceAccessControl && hasAnyStatusDevices,
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!tableParams?.pagination?.total) {
      setTableParams(tableParamsDefaultState(count))
    }
  }, [count])

  // The enabled/disabled states have different tab sets, so reset the active
  // tab when device access control is toggled to avoid landing on a tab that
  // no longer exists.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the DAC toggle
  useEffect(() => {
    setActiveTab(
      hasDeviceAccessControl ? DeviceStatus.pending : ALL_DEVICES_TAB
    )
    setStatus(DeviceStatus.pending)
  }, [hasDeviceAccessControl])

  const headerSubtitle = (
    <>
      {t(dacConstants.DAC_SUBTITLE)} {t(dacConstants.LEARN)}{' '}
      <HelpTextLink href={HELP_DISABLED} target="_blank" rel="noreferrer">
        {t(dacConstants.HOW_TO_SET_UP)}
        <ExportOutlined />
      </HelpTextLink>
    </>
  )

  const hasSpecificStatusDevices = !isLoading && !!devices?.length

  const handlePaginationChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      pagination
    })
  }

  const handleChangeTab = (tab: ActiveTab) => {
    setActiveTab(tab)
    setTableParams(tableParamsDefaultState())
    if (tab !== SIGNS_TAB && tab !== ALL_DEVICES_TAB) {
      setStatus(tab)
    }
  }

  const handleToggleDac = (checked: boolean) =>
    toggleFeature(
      'deviceAccessControl',
      checked,
      t(dacConstants.DEVICE_APPROVAL)
    )

  if (isLoadingAllStatuses) {
    return <Spinner data-testid="device-access-loading" />
  }

  if (!hasRolePermission) {
    return (
      <PageContentWrapper
        $gap={0}
        $paddingTop={24}
        $paddingX={24}
        $paddingBottom={24}
        data-testid="device-access-no-permission"
      >
        <PageHeader
          title={t(dacConstants.DEVICE_ACCESS_CONTROL)}
          subtitle={headerSubtitle}
          testId="device-access-header"
        />
        <EmptyState
          title={t(dacConstants.PSST)}
          description={t(dacConstants.NO_PERMISSIONS)}
          icon={<PermissionIcon />}
          testId="device-access-no-permission-empty"
        />
      </PageContentWrapper>
    )
  }

  return (
    <PageContentWrapper
      $gap={0}
      $paddingTop={24}
      $paddingX={24}
      $paddingBottom={24}
      data-testid="device-access-screen"
    >
      <PageHeader
        title={t(dacConstants.DEVICE_ACCESS_CONTROL)}
        subtitle={headerSubtitle}
        testId="device-access-header"
      />
      <ApprovalCard data-testid="device-approval-card">
        <Heading level={4}>{t(dacConstants.DEVICE_APPROVAL)}</Heading>
        <ApprovalDescription>
          {t(dacConstants.DEVICE_APPROVAL_DESC)} {t(dacConstants.LEARN)}{' '}
          <HelpTextLink href={HELP_ENABLED} target="_blank" rel="noreferrer">
            {t(dacConstants.HOW_DO_I_APPROVE)}
            <ExportOutlined />
          </HelpTextLink>
        </ApprovalDescription>
        <ToggleRow>
          <Switch
            checked={hasDeviceAccessControl}
            disabled={isSaving}
            onChange={handleToggleDac}
            data-cy="switch-device-access-control"
            data-testid="device-access-toggle"
          />
          <Text>
            {t(
              hasDeviceAccessControl
                ? dacConstants.ENABLED
                : dacConstants.DISABLED
            )}
          </Text>
        </ToggleRow>
      </ApprovalCard>
      {hasAnyStatusDevices && (
        <TableCard data-testid="device-access-content">
          <TabsWrapper>
            <StatusSwitch
              hasDeviceAccessControl={hasDeviceAccessControl}
              isLoading={isLoading}
              hasSpecificStatusDevices={hasSpecificStatusDevices}
              activeTab={activeTab}
              onChangeTab={handleChangeTab}
              testId="device-access-status-switch"
            />
          </TabsWrapper>
          {activeTab === SIGNS_TAB ? (
            <DeviceSignsPanel testId="device-access-signs-panel" />
          ) : (
            <>
              {(hasSpecificStatusDevices ||
                (hasAnyStatusDevices && !hasDeviceAccessControl)) && (
                <DevicesTable
                  devices={
                    hasDeviceAccessControl ? devices : devicesAllStatuses
                  }
                  handleTableChange={handlePaginationChange}
                  tableParams={tableParams}
                  hasDeviceAccessControl={hasDeviceAccessControl}
                  navigate={navigate}
                  refetch={refetch}
                  testId="device-access-table"
                />
              )}
              {isLoading && (
                <Spinner data-testid="device-access-table-loading" />
              )}
            </>
          )}
        </TableCard>
      )}
      {!hasAnyStatusDevices && (
        <EmptyState
          title={t(dacConstants.THIS_IS_WHERE_YOU)}
          icon={<NotStartedIcon />}
          testId="device-access-empty"
        />
      )}
    </PageContentWrapper>
  )
}

const ApprovalCard = styled(Card)`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ApprovalDescription = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`

// Tabs + table live in one card (design node 1:5498): tabs are the card's
// first row, then the grey header row, then the data rows. The card provides
// the outer radius, so the table itself is kept flush inside it.
const TableCard = styled(Card)`
  margin-top: 16px;
  padding: 0;
  overflow: hidden;
  border-radius: 16px;

  .ant-table {
    border-radius: 0;
  }
`

const TabsWrapper = styled.div`
  padding: 0 24px;

  .ant-tabs-tab {
    font-size: 16px;
  }

  /* Tabs are the card's first row; drop the default nav gap so they sit
     flush above the table's grey header row. */
  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }

  /* Extend the tab-nav divider out past the 24px padding so it spans the
     full card width (antd's ::before rule uses :where(), so this wins). */
  .ant-tabs-nav::before {
    left: -24px !important;
    right: -24px !important;
  }
`

export default DeviceAccessScreen
